
import { message, Select, Table } from 'antd';
import './section-model.less';
import { useEffect, useState } from 'react';
import { InputDropdown, Input, Modal, Dropdown } from 'bimui';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { ReactComponent as BoxSelectedSvg } from './icons/box-select.svg';
import { ReactComponent as BoxSvg } from './icons/box.svg';
import { createProjectSection, deleteProjectSectionBySectionIds, findProjectSectionsGroup } from '@/declare/api/project/project-section';
import { ProjectSection, Section, SectionCategory, SectionCategoryList, SectionType } from '@/declare/interface/project/section';
import { findSectionCategoryList, findSectionList, findSectionSources } from '@/declare/api/system-lib/section';
import { ReactComponent as RightArrowSvg } from './icons/right-arrow.svg';
import { ReactComponent as SearchSvg } from './icons/search.svg';
/**
 * 弹窗组件的属性接口
 */
interface IProps {
    showAddModal: boolean; // 控制弹窗是否显示
    projectSelectedItems?: Section[];  // 项目截面库的数据
    onCancel?: () => void; // 取消操作的回调
    onSave?: (selectedItems: ProjectSection) => void; // 新建保存操作的回调
}
/**
 * 截面添加弹窗组件
 * 用于编辑、添加项目截面
 */
export default function SectionModal(props: IProps) {
    const { showAddModal, onCancel, onSave, projectSelectedItems } = props;
    /**
     * 从 URL 中提取项目 UUID 参数
     */
    const { projectUuid } = useParams();
    /**
     * 显示系统库的截面列表数据
     */
    const [section, setSection] = useState<Section[]>([]);
    /**
     * 名称搜索/截面类型筛选/截面标准筛选后的截面列表数据
     */
    const [filteredSection, setFilteredSection] = useState<Section[]>([]);
    /**
     * 当前选中的系统库截面列表
     */
    const [selectedItems, setSelectedItems] = useState<Section[]>([]);
    /**
     * 之前选中的截面列表
     */
    const [oldSelectedItems, setOldSelectedItems] = useState<Section[]>([]);
    /**
     * 本次选中的截面列表
     */
    const [currentSelectedItems, setCurrentSelectedItems] = useState<Section[]>([]);

    // 一级分类列表
    const [categoryList, setCategoryList] = useState<SectionCategoryList>([]);

    // 当前选中的一级分类
    const [modalSelectedCategory, setModalSelectedCategory] = useState<SectionCategory | null>(null);

    // 当前选中的二级分类
    const [modalSelectedCode, setModalSelectedCode] = useState<SectionType | null>(null);

    /**
     * 截面标准下拉框的选项列表
     */
    const [standardList, setStandardList] = useState<{ label: string; value: string }[]>([]);
    /**
     * 截面标准下拉框的选中值
     */
    const [standard, setStandard] = useState(null);
    /**
     * 筛选输入的型号名称
     */
    const [sectionName, setSectionName] = useState<string>('');

    /**
     * 初始化截面系统库弹窗的状态
     */
    useEffect(() => {
        Promise.all([
            findSectionList(), // 获取 section 数据
            findSectionCategoryList(true), // 获取分类数据
            findSectionSources(), // 获取标准数据
        ])
            .then(([sectionRes, categoryRes, sourcesRes]) => {
                setSection(sectionRes.data || []);
                // setFilteredSection(sectionRes.data || []);
                setSelectedItems([...projectSelectedItems]); // 初始化已选用户
                setOldSelectedItems([...projectSelectedItems]); // 保存初始项目用户列表

                const data: SectionCategoryList = categoryRes.data || [];
                setCategoryList(data);

                const uniqueStandards = Array.from(new Set(sourcesRes.data || [])).map((source: string) => ({
                    label: source,
                    value: source,
                }));
                setStandardList(uniqueStandards); // 更新标准列表
            })
            .catch((error) => {
                message.error("加载数据失败，请稍后重试！");
                console.error(error);
            });
    }, []);

    /**
     * 初始化截面类型的默认选中
     */
    useEffect(() => {
        if (categoryList.length > 0) {
            // 默认选中第一个一级分类
            setModalSelectedCategory(categoryList[0]);
            if (categoryList[0]?.types?.length > 0) {
                // 默认选中第一个二级分类
                setModalSelectedCode(categoryList[0].types[0]);
            }
        }
    }, [categoryList]);
    /**
     * 选择截面
     * @param record 截面数据
     */
    const onBoxClick = (record: Section) => {
        const isSelected = selectedItems.some((item) => item.id === record.id);

        if (isSelected) {
            // 截面已选中 -> 取消选中
            setSelectedItems(selectedItems.filter((item) => item.id !== record.id));

            if (currentSelectedItems.some((item) => item.id === record.id)) {
                setCurrentSelectedItems(currentSelectedItems.filter((item) => item.id !== record.id));
            }
        } else {
            // 截面未选中 -> 选中
            setSelectedItems([...selectedItems, record]);

            if (!oldSelectedItems.some((item) => item.id === record.id)) {
                setCurrentSelectedItems([...currentSelectedItems, record]);
            }
        }
    };

    /**
     * 确认选择并执行关联截面到项目库的操作
     */
    const handleSave = () => {
        if (!selectedItems.length) {
            message.warning("请选择至少一个截面！");
            return;
        }

        const toAdd = currentSelectedItems.map((item) => item.id); // 本次新增截面的 UUID 列表
        const toRemove = oldSelectedItems
            .filter((oldItem) => !selectedItems.some((item) => item.id === oldItem.id))
            .map((item) => item.id); // 本次移除截面的 UUID 列表

        const addPromise = toAdd.length ? createProjectSection(projectUuid, toAdd) : Promise.resolve();
        const removePromise = toRemove.length ? deleteProjectSectionBySectionIds(projectUuid, toRemove) : Promise.resolve();

        Promise.all([addPromise, removePromise])
            .then(() => {
                findProjectSectionsGroup(projectUuid)
                    .then((res) => {
                        onSave?.(res.data); // 将最新的截面数据传递给父组件
                    })
                    .catch(() => {
                        message.error("获取最新的截面数据失败！");
                    });
            })
            .catch(() => {
                message.error("更新截面信息失败，请稍后重试！");
            });
    };


    /**
     * 更新搜索框中的截面名称的状态、根据输入内容筛选选择列表。
     * @param e 输入事件。
     */
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim(); // 获取输入值并去掉两端空格
        setSectionName(value); // 更新输入框状态
        // 过滤符合搜索条件的截面，目前于其他条件统一在filterSections方法中筛选
        // const filtered = section.filter(u => u.name.includes(value));
        // setFilteredSection(filtered);
    };


    /**
     * 截面标准下拉框选择操作
     * @param value 
     */
    const handleStandardChange = (value) => {
        setStandard(value);
    };


    useEffect(() => {
        filterSections();
    }, [sectionName, modalSelectedCategory, modalSelectedCode, standard, section]);

    const filterSections = () => {
        if (!section || !modalSelectedCategory) {
            // 如果数据尚未加载完成，跳过筛选
            return;
        }

        let filtered = [...section];

        // 根据用户输入的名称进行筛选
        if (sectionName) {
            filtered = filtered.filter((item) => item.name?.includes(sectionName));
        }

        // 根据选中的一级分类筛选
        if (modalSelectedCategory) {
            filtered = filtered.filter((item) => item.category === modalSelectedCategory.id);
        }

        // 根据选中的二级分类筛选
        if (modalSelectedCode) {
            filtered = filtered.filter((item) => item.type === modalSelectedCode.type);
        }

        // 根据选中的标准值筛选
        if (standard) {
            filtered = filtered.filter((item) => item.secSource === standard);
        }

        setFilteredSection(filtered);
    };


    return (
        <Modal
            open={showAddModal}
            width={459}
            mask={true}
            title='截面系统库'
            onSave={handleSave}
            onCancel={onCancel}
            wrapClassName='material-modal-container'
        >
            <div className='material-modal-header'>
                <Dropdown
                    options={standardList || []}
                    value={standard}
                    onChange={handleStandardChange}
                    style={{ width: '237px', height: '24px' }}
                />

                <div className="input-with-icon">
                    <Input
                        value={sectionName}
                        onChange={handleNameChange}
                    />
                    <span className="input-icon">
                        <SearchSvg />
                    </span>
                </div>
            </div>
            <div className='material-modal-body'>
                <div className='body-left'>
                    {categoryList.map((category) => (
                        <div
                            key={category.id}
                            className={classNames({
                                "category-item": true,
                                "active": modalSelectedCategory?.id === category.id,
                            })}
                            onClick={() => {
                                setModalSelectedCategory(category);
                                if (category.types?.length > 0) {
                                    setModalSelectedCode(category.types[0]); // 默认选中第一个类型
                                } else {
                                    setModalSelectedCode(null); // 如果没有类型，则清空选中
                                }
                            }}
                        >
                            <span>{category.name}</span>
                            <RightArrowSvg className="icon" />
                        </div>
                    ))}
                </div>
                <div className="body-middle">
                    {modalSelectedCategory?.types?.map((type) => (
                        <div
                            key={type.id}
                            className={classNames({
                                "middle-item": true,
                                "active": modalSelectedCode?.id === type.id,
                            })}
                            onClick={() => {
                                setModalSelectedCode(type);
                            }}
                        >
                            <span>{type.chName}</span>
                            <RightArrowSvg className="icon" />
                        </div>
                    ))}
                </div>

                <div className='body-right'>
                    <Table
                        rowKey="id" // 数据唯一标识
                        pagination={false} // 禁用分页
                        dataSource={filteredSection || []} // 数据源
                        bordered={false} // 无边框
                        className='tn-common-table' // 自定义表格样式
                        rowClassName={(row) =>
                            classNames({
                                "common-table-row": true,
                            })
                        }
                        sticky // 固定表头
                        showHeader={false} // 隐藏表头
                        onRow={(record) => ({
                            onClick: () => {
                                onBoxClick(record); // 点击整行时调用点击事件处理函数
                            },
                        })}
                    >
                        <Table.Column
                            dataIndex="name"  // 保留原有的数据源
                            title="型号"
                            width={`${130 / 340 * 100}%`}  // 设置列宽
                            ellipsis  // 启用文字省略
                            render={(value, record) => (

                                <div
                                    style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                    onClick={() => onBoxClick(record)}  // 保持点击选择行的功能
                                >
                                    {/* 保持选择框的显示 */}
                                    {!selectedItems?.some((item) => item.id === record.id) && (<BoxSvg style={{ cursor: 'pointer' }} />)}
                                    {selectedItems?.some((item) => item.id === record.id) && (<BoxSelectedSvg style={{ cursor: 'pointer' }} />)}
                                    <div className={classNames({ "text-active": currentSelectedItems.some((item) => item.id === record.id) })}
                                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right', marginLeft: '4px', }}                                >
                                        {value}
                                    </div>
                                </div>
                            )}
                        />

                    </Table>
                </div>
            </div>
        </Modal>
    )
}